import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  ErrorResponse,
  GET_COMPANY,
  PATCH_COMPANY,
} from '@frontend/data-access';
import { App, Button, Form, Input, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

interface FormValues {
  website: string;
  linkedin?: string;
  twitter?: string;
  facebook?: string;
}

function EditSocialAccountsForm({
  companyId,
  onFinish,
  onCancel,
}: {
  companyId: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const { data, loading } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [form] = Form.useForm();

  const handleCompleted = () => {
    notification.success({
      message: t('success_message'),
    });
    onFinish();
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error_message'),
      description: data.message,
    });
  };

  const [patchCompany, { loading: loadingPatch }] = useMutation(PATCH_COMPANY, {
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const handleOnFinish = async (values: FormValues) => {
    await patchCompany({
      variables: {
        input: {
          id: companyId,
          companyInfo: {
            ...values,
          },
        },
      },
    });
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  useEffect(() => {
    if (data?.company) {
      form.setFieldsValue({
        website: data.company.companyInfo?.website,
        linkedin: data.company.companyInfo?.linkedin,
        twitter: data.company.companyInfo?.twitter,
        facebook: data.company.companyInfo?.facebook,
      });
    }
  }, [data, form]);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleOnFinish}
      onFieldsChange={handleFormChange}
    >
      <Form.Item
        name="website"
        label={t('website_label')}
        rules={[
          { required: true, message: t('website_required_message') },
          {
            type: 'url',
            message: t('website_url_message'),
          },
        ]}
      >
        <Input
          placeholder={t('website_placeholder')}
          data-test="social-website-input"
          maxLength={255}
        />
      </Form.Item>
      <Form.Item name="linkedin" label={t('linkedin_label')}>
        <Input
          placeholder={t('linkedin_placeholder')}
          data-test="social-linkedin-input"
          maxLength={255}
        />
      </Form.Item>
      <Form.Item name="twitter" label={t('twitter_label')}>
        <Input
          placeholder={t('twitter_placeholder')}
          data-test="social-twitter-input"
          maxLength={255}
        />
      </Form.Item>
      <Form.Item name="facebook" label={t('facebook_label')}>
        <Input
          placeholder={t('facebook_placeholder')}
          data-test="social-facebook-input"
          maxLength={255}
        />
      </Form.Item>
      <div className="flex justify-end items-center gap-2">
        <Button disabled={loadingPatch} shape="round" onClick={onCancel}>
          {t('cancel_button')}
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          loading={loadingPatch}
          disabled={isIncomplete}
          shape="round"
        >
          {t('save_button')}
        </Button>
      </div>
    </Form>
  );
}

export default EditSocialAccountsForm;
